import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { ViewTypes } from 'src/helper/shared-types';
import {
  addNewView,
  deleteViewItemFromList,
  duplicateView,
  fetchAllViews,
  getFilterFieldValues,
  getSelectedGroupByDetails,
  getViewList,
  pinView,
  renameView,
  setAsDefaultView,
  updateView,
} from 'src/redux/list-view/api';
import { seedMarkets } from 'src/redux/list-view/reducers';
import type { InitialStateType } from 'src/redux/list-view/types';
import { getProfile } from 'src/redux/profile/api';

const extraReducers = (builder: ActionReducerMapBuilder<InitialStateType>) => {
  builder.addCase(getSelectedGroupByDetails().pending, (state: InitialStateType) => {
    state.fetchingGroupByDetails = true;
  });
  builder.addCase(getSelectedGroupByDetails().fulfilled, (state: InitialStateType) => {
    state.fetchingGroupByDetails = false;
  });
  builder.addCase(getSelectedGroupByDetails().rejected, (state: InitialStateType, action) => {
    // A canceled rejection belongs to a request that was superseded by a newer
    // in-flight one; flipping the single global flag to false here would clobber
    // that newer request's `pending` state and drop its spinner. Leave it alone.
    if ((action.payload as { actionType?: string })?.actionType === 'canceled') return;
    state.fetchingGroupByDetails = false;
  });

  builder.addCase(getFilterFieldValues().pending, (state: InitialStateType, action) => {
    if (action) {
      state.fetchingValuesLookUp[action?.meta.arg?.type] = true;
    }
  });
  builder.addCase(getFilterFieldValues().rejected, (state: InitialStateType, action) => {
    if (action) {
      state.fetchingValuesLookUp[action?.meta.arg?.type] = false;
    }
  });
  builder.addCase(getFilterFieldValues().fulfilled, (state: InitialStateType, action) => {
    if (action?.payload) {
      state.fetchingValuesLookUp[action?.meta.arg?.type] = false;
    }
  });

  // delete view item
  builder.addCase(deleteViewItemFromList().pending, (state: InitialStateType) => {
    state.isViewUpdating = true;
  });
  builder.addCase(deleteViewItemFromList().fulfilled, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  builder.addCase(deleteViewItemFromList().rejected, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  // rename view item
  builder.addCase(renameView().pending, (state: InitialStateType) => {
    state.isViewUpdating = true;
  });
  builder.addCase(renameView().fulfilled, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  builder.addCase(renameView().rejected, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  // duplicate view item
  builder.addCase(duplicateView().pending, (state: InitialStateType) => {
    state.isViewUpdating = true;
  });
  builder.addCase(duplicateView().fulfilled, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  builder.addCase(duplicateView().rejected, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  // add new  view item
  builder.addCase(addNewView().pending, (state: InitialStateType) => {
    state.isViewUpdating = true;
  });
  builder.addCase(addNewView().fulfilled, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  builder.addCase(addNewView().rejected, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  // update new  view item
  builder.addCase(updateView().pending, (state: InitialStateType) => {
    state.isViewUpdating = true;
  });
  builder.addCase(updateView().fulfilled, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  builder.addCase(updateView().rejected, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  // pin and unpin view
  builder.addCase(pinView().pending, (state: InitialStateType) => {
    state.isViewUpdating = true;
  });
  builder.addCase(pinView().fulfilled, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  builder.addCase(pinView().rejected, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  // set as default view
  builder.addCase(setAsDefaultView().pending, (state: InitialStateType) => {
    state.isViewUpdating = true;
  });
  builder.addCase(setAsDefaultView().fulfilled, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  builder.addCase(setAsDefaultView().rejected, (state: InitialStateType) => {
    state.isViewUpdating = false;
  });
  // per-module view list fallback (used when fetchAllViews endpoint is unavailable)
  builder.addCase(getViewList().pending, (state: InitialStateType, action) => {
    const viewType = action?.meta?.arg?.view_type as ViewTypes;
    // Only show the loading skeleton when the list is empty; avoids a skeleton
    // flash when the list is refreshed in the background with data already shown.
    if (state[viewType]?.viewList?.data?.length === 0) {
      state[viewType].viewList.isViewListLoading = true;
    }
  });
  builder.addCase(getViewList().fulfilled, (state: InitialStateType, action) => {
    const viewType = action?.meta?.arg?.view_type as ViewTypes;
    state[viewType].viewList.data = action?.payload;
    state[viewType].viewList.isViewListLoading = false;
  });
  builder.addCase(getViewList().rejected, (state: InitialStateType, action) => {
    const viewType = action?.meta?.arg?.view_type as ViewTypes;
    state[viewType].viewList.isViewListLoading = false;
  });
  // boot-time prefetch of all views.
  // isViewsPrefetchFailed signals that api/views/fetch-all is unavailable on this
  // environment, triggering per-module fallback fetches in ListViewWrapper and
  // useListViewDetailsApi. Reset it on each new attempt so retries start clean.
  builder.addCase(fetchAllViews().pending, (state: InitialStateType) => {
    state.isViewsPrefetching = true;
    state.isViewsPrefetchFailed = false;
  });
  builder.addCase(fetchAllViews().fulfilled, (state: InitialStateType) => {
    state.isViewsPrefetchFailed = false;
    // actual hydration is handled by the hydrateBootViewCache reducer dispatched in useUserProfile
  });
  // APX-478 — this case deliberately does NOT set `isViewsPrefetchFailed`, and moving it back
  // here would reintroduce a fixed bug. The flag is a TRIGGER: ListViewWrapper answers it with a
  // per-module fallback and escalates that fallback's own rejection into `notFound()`, which is
  // held in local state with no reset path. Since `fetchAllViews` now races `getProfile` instead
  // of following it, a rejection here may mean "the endpoint is unavailable" OR "the whole boot
  // is failing" — and only `useUserProfile` knows which, and only once the profile has answered.
  // Latching automatically would fire the cascade in the window BEFORE that verdict exists, and
  // nothing can retract it afterwards.
  //
  // So the verdict is dispatched explicitly from the `fetchAllViews` catch in `useUserProfile`
  // (`markViewsPrefetchFailed` / `resetViewsPrefetchState`). Safe because that hook is the only
  // dispatcher of this thunk in the app — verify that before adding a second one.
  builder.addCase(fetchAllViews().rejected, (state: InitialStateType) => {
    state.isViewsPrefetching = false;
  });

  // Initialize selectedMarkets once on profile load so the dropdown never flickers on navigation
  builder.addCase(getProfile().fulfilled, (state: InitialStateType, action) => {
    seedMarkets(
      state,
      (action.payload as { markets?: Array<{ id: string; name: string }> })?.markets,
    );
  });
};

export default extraReducers;
